import {
  AuthActions,
  useFetcherResult,
  useLocale,
  useLocalePath,
} from "@commercekit/remix";
import {
  type ErrorResponse,
  isRouteErrorResponse,
  useParams,
  useRouteError,
} from "@remix-run/react";
import * as Sentry from "@sentry/remix";
import { usePrismicLabels } from "~/hooks/usePrismicLabel";
import WvFormHiddenInputs from "./WvFormHiddenInputs";
import type { HTMLComponent } from "@commercekit/react";
import { useEffect, useRef } from "react";
import Wv404Page from "~/components/templates/Wv404Page";

type StatusHandler = (info: {
  error: ErrorResponse;
  params: Record<string, string | undefined>;
}) => JSX.Element | null;

export function getErrorMessage(error: unknown) {
  if (typeof error === "string") return error;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  console.error("Unable to get error message for error", error);
  return "Unknown Error";
}

function NotFoundError() {
  // Fallback 404 for routes that throw `notFound()` into this boundary (e.g. home
  // with a bad locale segment, thank-you, legacy product route). The common
  // page-flow 404s render the full Prismic page via loadNotFound; here we use
  // Wv404Page's built-in fallback (localized labels + banner) so we don't have to
  // ship the 404 document in every page's payload just for these rare cases.
  return <Wv404Page page={null} />;
}
const errorTranslations: {
  [key: string]: { heading: string; desc: string };
} = {
  nl: { heading: "Fout!", desc: "Oeps! Er is iets misgegaan." },
  be: { heading: "Fout!", desc: "Oeps! Er is iets misgegaan." },
  gb: { heading: "Error!", desc: "Oops! Something has gone wrong." },
  ie: { heading: "Error!", desc: "Oops! Something has gone wrong." },
  de: { heading: "Ups!", desc: "Etwas ist schief gelaufen." },
  fr: { heading: "Oups!", desc: "Un problème s'est produit." },
  pl: { heading: "Ups!", desc: "Coś poszło nie tak." },
  it: { heading: "Ops!", desc: "Qualcosa è andato storto." },
  pt: { heading: "Ops!", desc: "Algo correu mal." },
  es: { heading: "¡Uy!", desc: "Algo ha salido mal." },
  dk: { heading: "Ups!", desc: "Noget gik galt." },
  se: { heading: "Oops!", desc: "Något gick fel." },
};
function GeneralError({ error }: { error: ErrorResponse }) {
  const { $pt } = usePrismicLabels();

  const { locale } = useLocale();

  const heading =
    $pt("general_error_heading") || errorTranslations[locale]?.heading;

  const desc = $pt("general_error_desc") || errorTranslations[locale]?.desc;
  return (
    <div className="container mt-3 mt-lg-5 mb-5 pb-lg-5 text-center">
      <div className="content">
        <img
          src="https://images.prismic.io/wovar-rb2-dev/806d7ea9-d1aa-43ae-935e-d77031a150ae_error.png?auto=compress%2Cformat&width=640 640w, https://images.prismic.io/wovar-rb2-dev/806d7ea9-d1aa-43ae-935e-d77031a150ae_error.png?auto=compress%2Cformat&width=828 828w, https://images.prismic.io/wovar-rb2-dev/806d7ea9-d1aa-43ae-935e-d77031a150ae_error.png?auto=compress%2Cformat&width=1200 1200w, https://images.prismic.io/wovar-rb2-dev/806d7ea9-d1aa-43ae-935e-d77031a150ae_error.png?auto=compress%2Cformat&width=2048 2048w, https://images.prismic.io/wovar-rb2-dev/806d7ea9-d1aa-43ae-935e-d77031a150ae_error.png?auto=compress%2Cformat&width=3840 3840w"
          alt="Error"
          loading="lazy"
          width="351"
          height="159"
          className="mb-3 px-5 mx-auto h-auto"
          style={{ maxWidth: "361px" }}
        />
      </div>
      <h1 className="heading xxl">
        <span className="text--strong text--primary">
          ({(typeof error === "object" ? error.status : null) ?? 500})
        </span>{" "}
        {heading}
      </h1>
      <div className="content">
        <p>{desc}</p>
      </div>
    </div>
  );
}

function AuthError({ error }: { error: ErrorResponse }) {
  const localePath = useLocalePath();
  const logoutBtn = useRef<HTMLButtonElement>(null);

  const {
    fetcher: { Form },
  } = useFetcherResult();

  const LogoutForm: HTMLComponent = () => (
    <Form action={localePath("/customer/logout")} method="post">
      <WvFormHiddenInputs
        data={{
          _action: AuthActions.LOGOUT,
          redirect: `/customer/login?token=expired&redirect=${error.data}`,
          _root_refresh: true,
        }}
      />
      <button type="submit" hidden ref={logoutBtn} />
    </Form>
  );
  useEffect(() => {
    setTimeout(() => {
      logoutBtn.current?.click();
    }, 1000);
  }, []);
  return (
    <div className="container mt-3 mt-lg-5 mb-5 pb-lg-5 text-center">
      <LogoutForm />
      <div className="loader-container">
        <div className="lds-ellipsis">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
      </div>
    </div>
  );
}

export default function GeneralErrorBoundary({
  defaultStatusHandler = ({ error }) =>
    error.status === 404 ? (
      <NotFoundError />
    ) : error.status === 401 ? (
      <AuthError error={error}></AuthError>
    ) : (
      <GeneralError error={error} />
    ),
  statusHandlers,
  // @ts-expect-error this is okay
  unexpectedErrorHandler = (error) => <GeneralError error={error} />,
  skipSentryCapture = false,
}: {
  defaultStatusHandler?: StatusHandler;
  statusHandlers?: Record<number, StatusHandler>;
  unexpectedErrorHandler?: (error: unknown) => JSX.Element | null;
  skipSentryCapture?: boolean;
}) {
  const error = useRouteError();
  const params = useParams();

  if (!skipSentryCapture && typeof document !== "undefined") {
    if (isRouteErrorResponse(error)) {
      // 401/403/404 are expected, not bugs.
      if (error.status >= 500) {
        Sentry.captureMessage(
          `Route error ${error.status}: ${error.statusText}`,
          {
            level: "error",
            extra: { status: error.status, data: error.data },
          }
        );
      }
    } else {
      Sentry.captureException(error);
    }
    console.error(error);
  }

  return (
    <div className="container flex items-center justify-center p-20 text-h2">
      {/* Never let an error page (incl. a slow-render abort → 500) be indexed as
          content. React 19 hoists this <meta> into <head>. */}
      <meta name="robots" content="noindex" />
      {isRouteErrorResponse(error)
        ? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
            error,
            params,
          })
        : unexpectedErrorHandler(error)}
    </div>
  );
}
